import { z } from 'zod';

import {
  isMemberSubmittableStep,
  VerificationStepKeySchema,
} from '../verification-step-key/verification-step-key';

/**
 * Why a case was refused — **shown to the member** (`03-verification.md`).
 *
 * A closed set rather than free text because the member reads it in their own
 * locale: the client maps the code to translated copy (the api has no error
 * i18n), and a free-text-only rejection would reach an Arabic-first member in
 * whatever language the reviewer happened to type. `other` exists so a reviewer
 * is never forced into a code that does not fit, and it is the one case where
 * the note carries the whole explanation.
 */
export const VERIFICATION_DECISION_REASONS = [
  'id_unreadable',
  'id_expired',
  'selfie_unclear',
  'identity_mismatch',
  'interview_failed',
  'suspected_fraud',
  'policy_violation',
  'other',
] as const;

export const VerificationDecisionReasonSchema = z.enum(VERIFICATION_DECISION_REASONS);

export type VerificationDecisionReason = (typeof VERIFICATION_DECISION_REASONS)[number];

const NoteSchema = z.string().trim().min(1).max(2000).nullable();

/**
 * A reviewer's verdict.
 *
 * A **discriminated union**, so "approve" cannot carry a rejection reason and
 * "reject" cannot omit one. Expressing it as one optional-everything object
 * would push both checks into the service, where a missing branch is a rejection
 * the member is never told the reason for.
 */
export const VerificationDecisionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('approve'), note: NoteSchema }).strict(),
  z
    .object({
      action: z.literal('reject'),
      reasonCode: VerificationDecisionReasonSchema,
      note: NoteSchema,
    })
    .strict(),
]);

export type VerificationDecisionRequest = z.infer<typeof VerificationDecisionSchema>;

/**
 * Sending specific steps back to the member.
 *
 * The note is **mandatory**: this is the one decision whose entire purpose is to
 * tell somebody what to do differently, and "changes requested" with nothing to
 * change is a dead end the member cannot escape.
 *
 * Only member-submittable steps may be named — asking somebody to redo the
 * automated face match is an instruction they cannot follow.
 */
export const RequestChangesSchema = z
  .object({
    steps: z
      .array(VerificationStepKeySchema)
      .min(1)
      .refine((steps) => new Set(steps).size === steps.length, {
        message: 'A step may be requested only once.',
      })
      .refine((steps) => steps.every(isMemberSubmittableStep), {
        message: 'Only steps the member can submit may be sent back.',
      }),
    note: z.string().trim().min(1).max(2000),
  })
  .strict();

export type RequestChangesRequest = z.infer<typeof RequestChangesSchema>;

/**
 * Skipping a step for this case (audited, N22).
 *
 * The reason is required because a waiver is a reviewer overriding policy for
 * one member, and the audit row is worth nothing without it.
 */
export const WaiveStepSchema = z
  .object({
    reason: z.string().trim().min(1).max(1000),
  })
  .strict();

export type WaiveStepRequest = z.infer<typeof WaiveStepSchema>;
